import "@fortawesome/fontawesome-free/css/all.min.css";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Navigate, Route, Routes } from "react-router-dom";
import MainLayout from "./components/layouts/MainLayout/MainLayout";
import SearchProvider from "./contexts/searchContext";
import "./index.css";
import { publicRoutes } from "./routes/publicRoutes";
import "@fontsource/montserrat";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/400-italic.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/manrope/400.css";
import ScrollToTop from "./components/elements/ScrollToTop/ScrollToTop";
import PageNotFound from "./components/elements/PageNotFound/PageNotFound";
import ProjectDetailsPage from "./containers/Ourprojects/ProjectDetails";
import ProjectListPage from "./containers/Ourprojects/ProjectListPage";
import { CategoryProvider } from "./contexts/CategoryContext";
import CompletedSubcategoryPage from "./containers/Ourprojects/CompletedSubcategoryPage";
import SanityServiceDetailPage from "./containers/Services/SanityServiceDetailPage";
import SanityProductDetailPage from "./containers/Products/SanityProductDetailPage"; // ✅ Import product detail page
import GalleryCategoryPage from "./containers/Gallery/GalleryCategoryPage";
import ScrollProgressBar from "./components/ScrollProgressBar";
import LoaderWithText from "./components/Loader/LoaderWithText";
import { PageLoadingProvider } from "./contexts/PageLoadingContext";
import { usePageLoading } from "./hooks/usePageLoading";
import NavbarOld from "./components/elements/Navbar/NavbarOld";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import CookieConsentBanner from "./components/elements/CookieConsentBanner/CookieConsentBanner";

function AppContent() {
  const { isLoading, loadingPage } = usePageLoading();

  // Prerendered pages keep their saved HTML hidden until the app has started (see
  // scripts/prerender.mjs). React has replaced that HTML by now, so lift the cover.
  useEffect(() => {
    document.getElementById('root')?.removeAttribute('data-prerendered');
    document.getElementById('prerender-hide')?.remove();
  }, []);

  return (
    <div className="App" style={{ backgroundColor: '#eeeeee', minHeight: '100vh' }}>
      {/* One-time intro. The app renders underneath it (it is not hidden), so it adds no waiting. */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="intro"
            role="status"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-white flex items-center justify-center z-[10000]"
          >
            <div className="text-center flex flex-col items-center">
              <LoaderWithText text="SHEEL" size="large" animated={true} color="#25b09b" textColor="#ffffff" />
              <p className="mt-2 text-custom-secolor text-md text-center">
                {loadingPage === 'Welcome' ? `${loadingPage}...` : loadingPage ? `Loading ${loadingPage}...` : 'Loading...'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchProvider>
        <CategoryProvider>
          <CookieConsentProvider>
            <ScrollProgressBar />
            <Routes>
              <Route path="/" element={<MainLayout />}>
                {publicRoutes.map((route) => (
                  <Route key={route.path} path={route.path} element={route.component} />
                ))}
                <Route path="/projects" element={<ProjectListPage />} />
                <Route path="/projects/completed-subcategories" element={<CompletedSubcategoryPage />} />
                <Route path="/projects/:slug" element={<ProjectDetailsPage />} />

                <Route path="/services/:slug" element={<SanityServiceDetailPage />} />
                <Route path="/products/:slug" element={<SanityProductDetailPage />} />
                <Route path="/gallery/:category" element={<GalleryCategoryPage />} />

                {/* An old link that used to show an empty page */}
                <Route path="/ourprojects" element={<Navigate to="/projects" replace />} />

                {/* Anything else is a real "not found", kept out of search results */}
                <Route path="*" element={<PageNotFound />} />
              </Route>
            </Routes>

            <ScrollToTop />
            <CookieConsentBanner />
          </CookieConsentProvider>
        </CategoryProvider>
      </SearchProvider>
    </div>
  );
}

function App() {
  return (
    <PageLoadingProvider>
      <AppContent />
    </PageLoadingProvider>
  );
}

export default App;
